import { HttpMethod } from '../../constants';
function replaceEnterpriseId(enterpriseId: string | undefined): string {
  if (enterpriseId === undefined) {
    throw new Error('enterpriseId is required');
  }
  return this.path.replace(':enterpriseId', enterpriseId);
}
export const analyticEndpoints = {
  [HttpMethod.Get]: {
    AnalyticReports: {
      path: '/analytic-reports',
      route: () => '/analytic-reports'
    },
    AnalyticReport: {
      path: '/analytic-reports/:analyticReportId',
      route: (analyticReportId: string) => `/analytic-reports/${analyticReportId}`
    },
    Surveys: {
      path: '/enterprises/:enterpriseId/analytics/surveys',
      route: (enterpriseId: string) => `/enterprises/${enterpriseId}/analytics/surveys`
    },
    SurveyDetail: {
      path: '/enterprises/:enterpriseId/analytics/surveys/:surveyId',
      route: (surveyId: string) => `/surveys/${surveyId}`
    },
    MetabaseDashboard: {
      path: '/organizations/:enterpriseId/metabase/dashboard',
      route: replaceEnterpriseId
    },
    MetabaseCustomDashboard: {
      path: '/organizations/:enterpriseId/metabase/custom',
      route: replaceEnterpriseId
    },
    BuildingsFilters: {
      path: '/analytics/filters/buildings',
      route(): string {
        return this.path;
      }
    },
    WorksitesFilters: {
      path: '/analytics/filters/worksites',
      route(): string {
        return this.path;
      }
    },
    CitiesFilters: {
      path: '/analytics/filters/cities',
      route(): string {
        return this.path;
      }
    },
    AttributesFilters: {
      path: '/analytics/filters/attributes',
      route(): string {
        return this.path;
      }
    },
    SurveyQuestionResponses: {
      path: '/analytics/survey-account-answers'
    },
    SubEnterprisesFilters: {
      path: '/analytics/filters/sub-enterprises',
      route(): string {
        return this.path;
      }
    }
  }
};

/**
 * POST endpoints for api-analytics-rs. Kept separate from apiAnalyticEndpoints
 * because the dynamic analyticsApiCalls generator only handles GET methods.
 */
export const apiAnalyticPostEndpoints = {
  ModelingStatus: {
    path: '/analytics/modeling/status',
    route(): string {
      return this.path;
    }
  }
};

/**
 * Define all GET endpoints directed to api-analytics-rs. All route functions should have the same parameters
 * so it's easy to dynamically process those. This is an abstraction we are trying to do to make it easier
 * and faster to create new endpoints with hooks for analytics. We might need to refactor this in the future!
 */
export const apiAnalyticEndpoints = {
  [HttpMethod.Get]: {
    BuildingsCommuteIntervals: {
      path: '/analytics/buildings/commute-intervals',
      route(): string {
        return this.path;
      }
    },
    MapAccountsData: {
      path: '/analytics/accounts/map',
      route(): string {
        return this.path;
      }
    },
    CommuteDistanceDistribution: {
      path: '/analytics/distributions/distances',
      route(): string {
        return this.path;
      }
    },
    CommuteTimeDistribution: {
      path: '/analytics/distributions/times',
      route(): string {
        return this.path;
      }
    },
    EvSplit: {
      path: '/analytics/ev-split',
      route(): string {
        return this.path;
      }
    },
    VehicleOccupantsCount: {
      path: '/analytics/number-vehicle-occupants',
      route(): string {
        return this.path;
      }
    },
    AvgCommuteMetrics: {
      path: '/analytics/average-commute-metrics/all',
      route(): string {
        return this.path;
      }
    },
    ModeSplit: {
      path: '/analytics/category-mode-split',
      route(): string {
        return this.path;
      }
    },
    SOVRate: {
      path: '/analytics/rates/sov',
      route(): string {
        return this.path;
      }
    },
    TotalAnnualEmissions: {
      path: '/analytics/total-annual-emissions-commuting',
      route(): string {
        return this.path;
      }
    },
    AverageAnnualEmissionPerEmployee: {
      path: '/analytics/average-annual-emission-per-employee',
      route(): string {
        return this.path;
      }
    },
    EnterpriseAccountsCount: {
      path: '/enterprises/:enterpriseId/accounts/count',
      route: replaceEnterpriseId
    },
    DailyModeDistribution: {
      path: '/analytics/mode-distribution/daily',
      route(): string {
        return this.path;
      }
    },
    BuildingCapacityVsRemote: {
      path: '/analytics/building-capacity-remote-vs-commuting',
      route(): string {
        return this.path;
      }
    },
    NumberOfRemoteDays: {
      path: '/analytics/number-of-remote/daily',
      route(): string {
        return this.path;
      }
    },
    PotentialModesMapAccountsData: {
      path: '/analytics/modeling/accounts/map',
      route(): string {
        return this.path;
      }
    },
    ModelingPotentialModeImpactMetrics: {
      path: '/analytics/modeling/accounts/impact-metrics',
      route(): string {
        return this.path;
      }
    },
    ModelingPotentialModeShiftCounts: {
      path: '/analytics/modeling/accounts/potential-category/counts',
      route(): string {
        return this.path;
      }
    },
    /**
     * Exports
     */
    EmissionDataExport: {
      path: '/analytics/exports/detailed-emission-per-routes',
      route(): string {
        return this.path;
      }
    },
    SurveyAccountAnswersExport: {
      path: '/analytics/exports/detailed-survey-account-answers',
      route(): string {
        return this.path;
      }
    },
    // Shuttle modeling CSV exports built server-side (api-analytics-rs); the FE just
    // forwards the current selection + filters and downloads the returned CSV.
    ModelingShuttleRouteDetailsExport: {
      path: '/analytics/exports/modeling/shuttle-route-details',
      route(): string {
        return this.path;
      }
    },
    ModelingShuttleCommutersExport: {
      path: '/analytics/exports/modeling/shuttle-commuters',
      route(): string {
        return this.path;
      }
    },
    /**
     * Insights
     */
    CommuteDistanceDistributionInsightsMedian: {
      path: '/analytics/distributions/distances/insights/median',
      route(): string {
        return this.path;
      }
    },
    CommuteTimeDistributionInsightsLongerThan45Min: {
      path: '/analytics/distributions/times/insights/longer-than-45-min',
      route(): string {
        return this.path;
      }
    },
    EvSplitInsightsCountsPerDay: {
      path: '/analytics/ev-split/insights/counts-per-day',
      route(): string {
        return this.path;
      }
    },
    NumberOfVehicleOccupantsInsightsMean: {
      path: '/analytics/number-vehicle-occupants/insights/mean',
      route(): string {
        return this.path;
      }
    },
    ModeDistributionInsightsHighestNumberOfDriversPerDay: {
      path: '/analytics/mode-distribution/insights/highest-number-of-drivers-per-day',
      route(): string {
        return this.path;
      }
    },
    BuildingCapacityRemoteVsCommutingInsightsHighestCapacityPerDay: {
      path: '/analytics/building-capacity-remote-vs-commuting/insights/highest-capacity-per-day',
      route(): string {
        return this.path;
      }
    },
    BuildingsCommuteIntervalsInsightsHighestCapacityTimes: {
      path: '/analytics/buildings/commute-intervals/insights/highest-capacity-times',
      route(): string {
        return this.path;
      }
    },
    NumberOfRemoteDailyInsightsMostPopular: {
      path: '/analytics/number-of-remote/daily/insights/most-popular',
      route(): string {
        return this.path;
      }
    }
  }
};
export default analyticEndpoints;